import { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import * as api from '../lib/api';

// ── Estado inicial ─────────────────────────────────────────────

const INITIAL_STATE = {
  cargando: true,
  guardando: false,
  syncError: null,
  darkMode: false,
  empresaId: null, // Firestore document ID
  empresa: null,
  centrosTrabajo: [],
  centroActualIdx: 0,
  trabajadorActual: null,
  guiaActual: null,
  respuestasActuales: {},
  politicaData: {
    fechaElaboracion: new Date().toISOString().split("T")[0],
    nombreRepresentante: "", cargoRepresentante: "",
    mecanismosQuejas: "buzón de quejas confidencial y correo electrónico institucional",
    compromisosAdicionales: "", version: 0, historial: [], guardado: false,
  },
  mediosDifusion: [false, false, false, false],
  muestraSeleccionada: {},
};

// ── Action Types ───────────────────────────────────────────────

const ACTION_TYPES = {
  LOAD_STATE: 'LOAD_STATE',
  SET_CARGANDO: 'SET_CARGANDO',
  SET_DARK_MODE: 'SET_DARK_MODE',
  SET_EMPRESA: 'SET_EMPRESA',
  SET_EMPRESA_ID: 'SET_EMPRESA_ID',
  SET_CENTROS: 'SET_CENTROS',
  SET_CENTRO_ACTUAL: 'SET_CENTRO_ACTUAL',
  ADD_CENTRO: 'ADD_CENTRO',
  UPDATE_CENTRO: 'UPDATE_CENTRO',
  DELETE_CENTRO: 'DELETE_CENTRO',
  ADD_TRABAJADOR: 'ADD_TRABAJADOR',
  IMPORT_TRABAJADORES: 'IMPORT_TRABAJADORES',
  DELETE_TRABAJADOR: 'DELETE_TRABAJADOR',
  UPDATE_TRABAJADOR: 'UPDATE_TRABAJADOR',
  SET_CUESTIONARIO_ACTIVO: 'SET_CUESTIONARIO_ACTIVO',
  SET_RESPUESTAS: 'SET_RESPUESTAS',
  GUARDAR_CUESTIONARIO: 'GUARDAR_CUESTIONARIO',
  CANCELAR_CUESTIONARIO: 'CANCELAR_CUESTIONARIO',
  SET_FICHA: 'SET_FICHA',
  SET_CONSENTIMIENTO: 'SET_CONSENTIMIENTO',
  SET_POLITICA: 'SET_POLITICA',
  SET_MEDIOS_DIFUSION: 'SET_MEDIOS_DIFUSION',
  SET_PROGRAMA: 'SET_PROGRAMA',
  SET_MUESTRA: 'SET_MUESTRA',
  SET_SYNC_ERROR: 'SET_SYNC_ERROR',
  CLEAR_ALL: 'CLEAR_ALL',
};

// Estos actions se disparan directo desde onChange de un <input> (Política,
// Programa) — sin debounce, cada tecla escrita mandaría un PUT a Firestore.
const DEBOUNCED_TYPES = new Set([ACTION_TYPES.SET_POLITICA, ACTION_TYPES.SET_PROGRAMA]);

// ── Reducer (unchanged — handles local state instantly) ────────

function appReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.LOAD_STATE:
      return { ...state, ...action.payload, cargando: false };
    case ACTION_TYPES.SET_CARGANDO:
      return { ...state, cargando: action.payload };
    case ACTION_TYPES.SET_DARK_MODE:
      return { ...state, darkMode: action.payload };
    case ACTION_TYPES.SET_EMPRESA:
      return { ...state, empresa: action.payload };
    case ACTION_TYPES.SET_EMPRESA_ID:
      return { ...state, empresaId: action.payload };
    case ACTION_TYPES.SET_CENTROS:
      return { ...state, centrosTrabajo: action.payload };
    case ACTION_TYPES.SET_CENTRO_ACTUAL:
      return { ...state, centroActualIdx: action.payload };
    case ACTION_TYPES.ADD_CENTRO: {
      const centros = [...state.centrosTrabajo, action.payload];
      return { ...state, centrosTrabajo: centros, centroActualIdx: centros.length - 1 };
    }
    case ACTION_TYPES.UPDATE_CENTRO: {
      const { idx, data } = action.payload;
      return { ...state, centrosTrabajo: state.centrosTrabajo.map((c, i) => i === idx ? { ...c, ...data } : c) };
    }
    case ACTION_TYPES.DELETE_CENTRO: {
      const centros = state.centrosTrabajo.filter((_, i) => i !== action.payload);
      return { ...state, centrosTrabajo: centros, centroActualIdx: Math.min(state.centroActualIdx, Math.max(centros.length - 1, 0)) };
    }
    case ACTION_TYPES.ADD_TRABAJADOR: {
      const { centroIdx, trabajador } = action.payload;
      return { ...state, centrosTrabajo: state.centrosTrabajo.map((c, i) => i !== centroIdx ? c : { ...c, trabajadores: [...(c.trabajadores || []), trabajador] }) };
    }
    case ACTION_TYPES.IMPORT_TRABAJADORES: {
      const { centroIdx, trabajadores } = action.payload;
      return { ...state, centrosTrabajo: state.centrosTrabajo.map((c, i) => i !== centroIdx ? c : { ...c, trabajadores: [...(c.trabajadores || []), ...trabajadores] }) };
    }
    case ACTION_TYPES.DELETE_TRABAJADOR: {
      const { centroIdx, trabajadorId } = action.payload;
      return { ...state, centrosTrabajo: state.centrosTrabajo.map((c, i) => {
        if (i !== centroIdx) return c;
        const trabajadores = (c.trabajadores || []).filter(t => t.id !== trabajadorId);
        const cuestionariosCompletos = { ...(c.cuestionariosCompletos || {}) };
        const fichas = { ...(c.fichas || {}) };
        const consentimientos = { ...(c.consentimientos || {}) };
        delete cuestionariosCompletos[`${trabajadorId}_guiaI`];
        delete cuestionariosCompletos[`${trabajadorId}_guiaII`];
        delete cuestionariosCompletos[`${trabajadorId}_guiaIII`];
        delete fichas[trabajadorId]; delete consentimientos[trabajadorId];
        return { ...c, trabajadores, cuestionariosCompletos, fichas, consentimientos };
      }) };
    }
    case ACTION_TYPES.UPDATE_TRABAJADOR: {
      const { centroIdx: cIdx, trabajadorId: tId, data } = action.payload;
      return { ...state, centrosTrabajo: state.centrosTrabajo.map((c, i) => {
        if (i !== cIdx) return c;
        return { ...c, trabajadores: (c.trabajadores || []).map(t => t.id === tId ? { ...t, ...data } : t) };
      }) };
    }
    case ACTION_TYPES.SET_CUESTIONARIO_ACTIVO:
      return { ...state, trabajadorActual: action.payload.trabajador, guiaActual: action.payload.guia, respuestasActuales: action.payload.respuestas || {} };
    case ACTION_TYPES.SET_RESPUESTAS:
      return { ...state, respuestasActuales: action.payload };
    case ACTION_TYPES.GUARDAR_CUESTIONARIO: {
      const { centroIdx, trabajadorId, guia, respuestas } = action.payload;
      const key = `${trabajadorId}_guia${guia}`;
      return { ...state, centrosTrabajo: state.centrosTrabajo.map((c, i) => i !== centroIdx ? c : { ...c, cuestionariosCompletos: { ...(c.cuestionariosCompletos || {}), [key]: respuestas } }), trabajadorActual: null, guiaActual: null, respuestasActuales: {} };
    }
    case ACTION_TYPES.CANCELAR_CUESTIONARIO:
      return { ...state, trabajadorActual: null, guiaActual: null, respuestasActuales: {} };
    case ACTION_TYPES.SET_FICHA: {
      const { centroIdx, trabajadorId, ficha } = action.payload;
      return { ...state, centrosTrabajo: state.centrosTrabajo.map((c, i) => i !== centroIdx ? c : { ...c, fichas: { ...(c.fichas || {}), [trabajadorId]: ficha } }) };
    }
    case ACTION_TYPES.SET_CONSENTIMIENTO: {
      const { centroIdx, trabajadorId, data } = action.payload;
      return { ...state, centrosTrabajo: state.centrosTrabajo.map((c, i) => i !== centroIdx ? c : { ...c, consentimientos: { ...(c.consentimientos || {}), [trabajadorId]: data } }) };
    }
    case ACTION_TYPES.SET_POLITICA:
      return { ...state, politicaData: action.payload };
    case ACTION_TYPES.SET_MEDIOS_DIFUSION:
      return { ...state, mediosDifusion: action.payload };
    case ACTION_TYPES.SET_PROGRAMA: {
      const { centroIdx, data } = action.payload;
      return { ...state, centrosTrabajo: state.centrosTrabajo.map((c, i) => i !== centroIdx ? c : { ...c, programaIntervencion: data }) };
    }
    case ACTION_TYPES.SET_MUESTRA:
      return { ...state, muestraSeleccionada: action.payload };
    case ACTION_TYPES.SET_SYNC_ERROR:
      return { ...state, syncError: action.payload };
    case ACTION_TYPES.CLEAR_ALL:
      api.clearStoredEmpresaId();
      return { ...INITIAL_STATE, cargando: false };
    default:
      return state;
  }
}

// ── API sync: persist to Firestore after specific actions ──────

async function syncToAPI(state, action, rawDispatch) {
  const eid = state.empresaId;
  if (!eid) return; // No empresa yet, skip

  const centro = state.centrosTrabajo[state.centroActualIdx];
  const cid = centro?.id;

  try {
    switch (action.type) {
      case ACTION_TYPES.SET_EMPRESA:
        await api.saveEmpresa(eid, action.payload);
        break;

      // ADD_CENTRO, ADD_TRABAJADOR, IMPORT_TRABAJADORES y DELETE_TRABAJADOR
      // NO tienen caso aquí a propósito: sus páginas (RegistroCentroPage,
      // TrabajadoresPage) ya hacen `await api.xxx(...)` ellas mismas ANTES de
      // despachar la acción — para poder mostrar el límite de plan (403) de
      // forma síncrona. Si este switch también los manejara, cada alta/borrado
      // dispararía una segunda llamada redundante que, justo en el límite del
      // plan, puede fallar con un 403 aunque la primera ya haya tenido éxito —
      // mostrando un error falso de "no se pudo guardar" tras un guardado real.

      case ACTION_TYPES.SET_FICHA:
        if (cid || action.payload.centroIdx !== undefined) {
          const c = state.centrosTrabajo[action.payload.centroIdx];
          if (c) await api.saveFicha(eid, c.id, action.payload.trabajadorId, action.payload.ficha);
        }
        break;

      case ACTION_TYPES.SET_CONSENTIMIENTO:
        if (action.payload.centroIdx !== undefined) {
          const c = state.centrosTrabajo[action.payload.centroIdx];
          if (c) await api.saveConsentimiento(eid, c.id, action.payload.trabajadorId, action.payload.data);
        }
        break;

      case ACTION_TYPES.GUARDAR_CUESTIONARIO: {
        const c = state.centrosTrabajo[action.payload.centroIdx];
        const key = `${action.payload.trabajadorId}_guia${action.payload.guia}`;
        if (c) await api.saveCuestionario(eid, c.id, key, action.payload.respuestas);
        break;
      }

      case ACTION_TYPES.SET_POLITICA:
        await api.savePolitica(eid, action.payload);
        break;

      case ACTION_TYPES.SET_PROGRAMA: {
        const centroId = state.centrosTrabajo[action.payload.centroIdx]?.id;
        if (centroId) await api.savePrograma(eid, action.payload.data, centroId);
        break;
      }

      case ACTION_TYPES.SET_MEDIOS_DIFUSION:
      case ACTION_TYPES.SET_MUESTRA:
      case ACTION_TYPES.SET_DARK_MODE:
        await api.saveSettings(eid, {
          mediosDifusion: state.mediosDifusion,
          muestraSeleccionada: state.muestraSeleccionada,
          darkMode: state.darkMode,
        });
        break;
    }
    rawDispatch?.({ type: ACTION_TYPES.SET_SYNC_ERROR, payload: null });
  } catch (err) {
    console.error(`[API Sync] Error on ${action.type}:`, err.message);
    // Optimistic: state already updated locally; surface the failure so the
    // user knows this change didn't actually persist to the backend.
    rawDispatch?.({ type: ACTION_TYPES.SET_SYNC_ERROR, payload: err.message || 'No se pudo guardar el último cambio' });
  }
}

// ── Context + Provider ─────────────────────────────────────────

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [state, rawDispatch] = useReducer(appReducer, INITIAL_STATE);
  const stateRef = useRef(state);
  stateRef.current = state;
  const { user, loading: authLoading } = useAuth();
  const syncTimersRef = useRef({});

  // Dispatch wrapper: update local state + sync to API
  const dispatch = useCallback((action) => {
    rawDispatch(action);
    if (DEBOUNCED_TYPES.has(action.type)) {
      clearTimeout(syncTimersRef.current[action.type]);
      syncTimersRef.current[action.type] = setTimeout(() => {
        syncToAPI(stateRef.current, action, rawDispatch);
      }, 600);
    } else {
      setTimeout(() => {
        syncToAPI(stateRef.current, action, rawDispatch);
      }, 0);
    }
  }, []);

  // ── Load state AFTER auth is ready ───────────────────────
  useEffect(() => {
    if (authLoading) return; // Wait for auth
    if (!user) {
      rawDispatch({ type: ACTION_TYPES.SET_CARGANDO, payload: false });
      return;
    }

    const load = async () => {
      // Set user UID for localStorage key isolation
      api.setCurrentUid(user.uid);
      let empresaId = api.getStoredEmpresaId();

      // If no empresaId in localStorage, check Firestore for user's empresas
      if (!empresaId) {
        try {
          const empresas = await api.getMisEmpresas();
          if (empresas && empresas.length > 0) {
            empresaId = empresas[0].id;
            api.setStoredEmpresaId(empresaId);
          }
        } catch (e) {
          console.error('[Load] Failed to find empresas:', e.message);
        }
      }

      if (!empresaId) {
        rawDispatch({ type: ACTION_TYPES.SET_CARGANDO, payload: false });
        return;
      }

      // Small delay to ensure token is ready
      await new Promise(r => setTimeout(r, 500));

      try {
        const data = await api.loadState(empresaId);
        if (data && data.exists) {
          rawDispatch({
            type: ACTION_TYPES.LOAD_STATE,
            payload: {
              empresaId,
              empresa: data.empresa,
              centrosTrabajo: (data.centrosTrabajo || []).map(c => ({
                ...c,
                programaIntervencion: c.programaIntervencion || { acciones: [], fechaCreacion: '', responsableGeneral: '' },
              })),
              centroActualIdx: 0,
              politicaData: data.politicaData || INITIAL_STATE.politicaData,
              mediosDifusion: data.mediosDifusion || [false, false, false, false],
              muestraSeleccionada: data.muestraSeleccionada || {},
              darkMode: data.darkMode || false,
            },
          });
        } else {
          api.clearStoredEmpresaId();
          rawDispatch({ type: ACTION_TYPES.SET_CARGANDO, payload: false });
        }
      } catch (err) {
        console.error('[Load] Failed to load from API:', err.message);
        // If 403 (wrong owner), clear stored empresaId and try to find user's own empresas
        if (err.message?.includes('403') || err.message?.includes('No autorizado') || err.message?.includes('No tienes acceso') || err.message?.includes('owner')) {
          api.clearStoredEmpresaId();
          try {
            const empresas = await api.getMisEmpresas();
            if (empresas && empresas.length > 0) {
              const myEmpresaId = empresas[0].id;
              api.setStoredEmpresaId(myEmpresaId);
              const data = await api.loadState(myEmpresaId);
              if (data && data.exists) {
                rawDispatch({
                  type: ACTION_TYPES.LOAD_STATE,
                  payload: {
                    empresaId: myEmpresaId,
                    empresa: data.empresa,
                    centrosTrabajo: (data.centrosTrabajo || []).map(c => ({
                      ...c,
                      programaIntervencion: c.programaIntervencion || { acciones: [], fechaCreacion: '', responsableGeneral: '' },
                    })),
                    centroActualIdx: 0,
                    politicaData: data.politicaData || INITIAL_STATE.politicaData,
                    mediosDifusion: data.mediosDifusion || [false, false, false, false],
                    muestraSeleccionada: data.muestraSeleccionada || {},
                    darkMode: data.darkMode || false,
                  },
                });
                return;
              }
            }
          } catch (e) { console.error('[Load] Retry failed:', e.message); }
        }
        rawDispatch({ type: ACTION_TYPES.SET_CARGANDO, payload: false });
      }
    };
    load();
  }, [user, authLoading]);

  return (
    <AppContext.Provider value={{ state, dispatch, ACTION_TYPES }}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppState() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppState must be used inside AppProvider');
  return ctx;
}

export { ACTION_TYPES };
